import type { ChartAnnotation } from 'tacho-activity-chart';
import type { Dailychartinput } from 'app/services/rszoft/dailychart/models/chart.model';

export interface DailyReportAnnotationOptions {
    /** Driver tab: offences; vehicle tab: faults and driving without a card. */
    isDriver: boolean;
    /** Translation lookup bound to the report's current language. */
    translate: (key: string) => string;
    /** Length of this local day in seconds (DST days are not 24 h). */
    dayLengthSeconds?: number;
}

/**
 * The report-only data of one day, in the chart's annotation format.
 *
 * The daily report shows the day's events as a list-only annotation: the unique
 * event names, enumerated on a single line under the chart. The entries do not
 * carry times or descriptions, so the chart neither plots them (no bands,
 * markers or name chips) nor shows anything else under the day.
 */
export function buildDailyReportAnnotations(
    input: Dailychartinput,
    options: DailyReportAnnotationOptions,
): ChartAnnotation[] {
    const t = (key: string, fallback: string): string => {
        const value = options.translate(key);
        return value && value !== key ? value : fallback;
    };

    const names: string[] = [];

    if (input.noDataForToday) {
        names.push(t('Report.DailyChart.noDownloadedDataForToday', 'No downloaded data for today'));
    }

    if ((input.workingTimeRows || []).length > 0) {
        names.push(t('Report.DailyChart.series5Name', 'Shift'));
    }

    if (options.isDriver) {
        for (const offence of input.offences || []) {
            if (!offence?.type) { continue; }
            names.push(t('Report.DailyChart.' + offence.type, offence.type));
        }
    } else {
        // Faults without a fault code are shown as driving without a card.
        if ((input.drivingsWithoutCard || []).length > 0) {
            names.push(t('Report.DailyChart.DrivingWithoutCard', 'Driving without card'));
        }
        for (const fault of input.faults || []) {
            names.push(fault?.eventFaultType
                ? t('Report.WeeklyTable.FaultCode.' + fault.eventFaultType, t('Report.DailyChart.Fault', 'Fault'))
                : t('Report.DailyChart.DrivingWithoutCard', 'Driving without card'));
        }
    }

    const dayStart = Math.floor(input.dateOfChart / 1000);
    const dayEnd = dayStart + (options.dayLengthSeconds || 86400);
    const lastDownloadedAt = input.lastDownloadedTime && input.lastDownloadedTime > 0
        ? input.lastDownloadedTime / 1000
        : null;
    if (lastDownloadedAt !== null && lastDownloadedAt >= dayStart && lastDownloadedAt <= dayEnd) {
        names.push(t('LastDownload', 'Last download'));
    }

    return toDailyReportEventRow(names.map(name => ({ type: 'none', name })));
}

/**
 * Collapses a day's report entries into the one row the report shows under the
 * day chart: the unique event names, in a single line, with no times and no
 * descriptions.
 *
 * The returned entry is always `type: 'none'`, which the chart renders as a
 * list-only row — it is never plotted as a band, marker or name chip on the
 * lane. The daily report's events therefore stay below the chart even when a
 * caller hands in timed entries.
 */
export function toDailyReportEventRow(annotations: ChartAnnotation[]): ChartAnnotation[] {
    const names = uniqueNames((annotations || []).map(annotation => annotation?.name));
    if (names.length === 0) { return []; }

    return [{ type: 'none', name: names.join(', ') }];
}

/**
 * Trims the names and keeps the first spelling of each one, so a duplicated
 * event is listed once. Order is the order the report produced.
 */
function uniqueNames(names: Array<string | null | undefined>): string[] {
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const name of names) {
        const label = (name || '').trim();
        if (!label) { continue; }
        const key = label.toLowerCase();
        if (seen.has(key)) { continue; }
        seen.add(key);
        unique.push(label);
    }
    return unique;
}
